/**
 * Settings Page Component
 * 
 * Main settings page with nested routing
 * Uses SettingsLayout for two-column layout with sidebar navigation
 * 
 * @see specs/003-categorisation-feature/spec.md (Part 1: Settings Page Expansion)
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SettingsLayout } from './settings/SettingsLayout';
import { CategorisationPage } from './settings/CategorisationPage';
import { ProfilePage } from './settings/ProfilePage';
import { AccountSettingsPage } from './settings/AccountSettingsPage';
import { SecurityPrivacyPage } from './settings/SecurityPrivacyPage';
import { PageHeader } from '../components/PageHeader';
import { AiConfigurationPage } from './settings/ai/AiConfigurationPage';
import { Bell, Zap } from 'lucide-react';

export { AiConfigurationPage };

function FinancialAccountsPage(): React.ReactElement {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Financial Accounts"
        description="Manage financial accounts"
        helpSlug="settings"
      />
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Financial accounts coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationsPage(): React.ReactElement {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Notification preferences"
        helpSlug="settings"
      />
      
      {/* Coming Soon Badge */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800/50 shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-transparent to-blue-400/10"></div>
        <div className="relative px-6 py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-4 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-400/20 blur-xl rounded-full"></div>
                <div className="relative bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-2xl shadow-lg">
                  <Bell className="h-8 w-8 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full border border-purple-200 dark:border-purple-700 shadow-sm">
              <span className="h-2 w-2 bg-purple-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent">
                Coming Soon
              </span>
            </div>
            <h3 className="mt-6 text-xl font-bold text-gray-900 dark:text-gray-100">
              Notification Settings
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-md">
              We're creating a comprehensive notification system. Stay tuned for customizable alerts, reminders, and real-time updates!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RulesPage(): React.ReactElement {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Rules"
        description="Transaction automation rules"
        helpSlug="settings"
      />
      
      {/* Coming Soon Badge */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800/50 shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-transparent to-blue-400/10"></div>
        <div className="relative px-6 py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-4 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-400/20 blur-xl rounded-full"></div>
                <div className="relative bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-2xl shadow-lg">
                  <Zap className="h-8 w-8 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full border border-purple-200 dark:border-purple-700 shadow-sm">
              <span className="h-2 w-2 bg-purple-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent">
                Coming Soon
              </span>
            </div>
            <h3 className="mt-6 text-xl font-bold text-gray-900 dark:text-gray-100">
              Transaction Rules
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-md">
              We're building powerful automation rules. Get ready to automate categorization, tagging, and transaction management!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage(): React.ReactElement {
  return (
    <SettingsLayout>
      <Routes>
        <Route index element={<Navigate to="/settings/profile" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="account" element={<AccountSettingsPage />} />
        <Route path="financial-accounts" element={<FinancialAccountsPage />} />
        <Route path="categorisation/*" element={<CategorisationPage />} />
        <Route path="ai/*" element={<AiConfigurationPage />} />
        <Route path="security" element={<SecurityPrivacyPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="rules" element={<RulesPage />} />
      </Routes>
    </SettingsLayout>
  );
}
